import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useState, useEffect } from 'react'
import { 
  Store, 
  Package, 
  CreditCard, 
  Globe, 
  Shield, 
  Zap,
  Check,
  ArrowRight,
  Star,
  Eye,
  ChevronRight,
  TrendingUp,
  ImageIcon,
  MessageCircle,
  Share2,
  Link2,
  Crown,
  Palette,
  BarChart3,
  Lock,
} from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { usePublicArticles, usePublicTestimonials, type ArticlePreview, type PublicTestimonial } from '../hooks/useApi'
import { MessageSquareQuote, Newspaper } from 'lucide-react'
import { PLANS } from '../lib/plans'
import { getImageUrl } from '../lib/catalogUrl'
import { SiteFooter } from '../components/SiteFooter'

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 }
  }
}

const features = [
  {
    icon: Store,
    title: 'Profil Usaha Profesional',
    desc: 'Tampilkan logo, nama usaha, dan informasi lengkap agar pelanggan lebih percaya.',
    color: 'bg-blue-500'
  },
  {
    icon: Package,
    title: 'Katalog Produk Lengkap',
    desc: 'Tambahkan produk dengan foto, harga, dan deskripsi — semua dalam satu halaman.',
    color: 'bg-green-500'
  },
  {
    icon: CreditCard,
    title: 'Terima Pembayaran Online',
    desc: 'Pelanggan bisa bayar langsung lewat katalog Anda tanpa ribet.',
    color: 'bg-purple-500'
  },
  {
    icon: Globe,
    title: 'Link Khusus Usaha Anda',
    desc: 'Dapatkan link unik yang mudah dibagikan ke WhatsApp, Instagram, dan media sosial.',
    color: 'bg-orange-500'
  },
  {
    icon: Shield,
    title: 'Data Aman & Terjaga',
    desc: 'Informasi usaha dan produk Anda tersimpan dengan aman.',
    color: 'bg-red-500'
  },
  {
    icon: Zap,
    title: 'Buka Cepat di HP',
    desc: 'Katalog ringan dan cepat dibuka, cocok untuk pelanggan yang browsing dari HP.',
    color: 'bg-yellow-500'
  }
]

const painPoints = [
  {
    icon: ImageIcon,
    text: 'Harus kirim foto produk satu per satu ke customer'
  },
  {
    icon: MessageCircle,
    text: 'Customer sering bertanya produk yang sama berulang'
  },
  {
    icon: Share2,
    text: 'Informasi usaha tersebar di WhatsApp dan Instagram'
  },
  {
    icon: Link2,
    text: 'Sulit menampilkan semua produk dalam satu link'
  }
]

function formatArticleDate(value: string | null) {
  if (!value) return ''
  return new Date(value).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
}

type CatalogDemoTheme = { page: string; hero: string; accent: string; cta: string }

const CATALOG_DEMOS: {
  id: string
  name: string
  badge?: string
  badgeClass: string
  url: string
  theme: CatalogDemoTheme
  highlights: string[]
  locked?: string[]
}[] = [
  {
    id: 'free',
    name: 'Gratis',
    badgeClass: 'bg-gray-500 text-white',
    url: 'umkmindonesia.my.id/catalog/toko-anda',
    theme: { page: '#f8f6f2', hero: '#ffd76a', accent: '#102a5c', cta: '#0f2b60' },
    highlights: ['Tema biru default', 'Maks. 5 produk', 'Link katalog standar'],
    locked: ['Tanpa badge', 'Tanpa subdomain', 'Tanpa banner promo'],
  },
  {
    id: 'basic',
    name: 'Basic',
    badge: 'UMKM Terverifikasi',
    badgeClass: 'bg-blue-600 text-white',
    url: 'umkmindonesia.my.id/catalog/toko-anda',
    theme: { page: '#f0fdf4', hero: '#bbf7d0', accent: '#15803d', cta: '#14532d' },
    highlights: ['Warna katalog bisa diubah', 'Badge terverifikasi', 'Artikel usaha', 'Maks. 50 produk'],
    locked: ['Tanpa subdomain sendiri'],
  },
  {
    id: 'premium',
    name: 'Premium',
    badge: 'Premium',
    badgeClass: 'bg-amber-500 text-white',
    url: 'toko-anda.umkmindonesia.my.id',
    theme: { page: '#f5f3ff', hero: '#ddd6fe', accent: '#6d28d9', cta: '#4c1d95' },
    highlights: [
      'Subdomain sendiri',
      'Banner & video promo',
      'Statistik pengunjung',
      'Tampil di homepage',
      'Produk tanpa batas',
    ],
  },
]

function CatalogPlanDemo({ demo }: { demo: (typeof CATALOG_DEMOS)[0] }) {
  const { theme } = demo
  return (
    <div className="rounded-2xl border-2 border-gray-200 overflow-hidden bg-white shadow-lg h-full flex flex-col">
      <div className={`px-4 py-2 text-sm font-bold text-center ${demo.badgeClass}`}>
        Paket {demo.name}
      </div>
      <div className="p-3 flex-1" style={{ backgroundColor: theme.page }}>
        <div className="rounded-lg bg-white/90 border border-gray-200/80 p-2 mb-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg shrink-0" style={{ backgroundColor: theme.accent }} />
            <span className="text-xs font-semibold text-gray-800 truncate">Nama Toko</span>
          </div>
          {demo.badge ? (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${demo.badgeClass}`}>
              {demo.badge}
            </span>
          ) : (
            <span className="text-[10px] text-gray-400 shrink-0">—</span>
          )}
        </div>
        {demo.id === 'premium' && (
          <div className="h-6 rounded-md mb-2 bg-gradient-to-r from-amber-400 to-orange-400 text-[10px] text-white flex items-center justify-center font-medium">
            Banner Promo
          </div>
        )}
        <div className="rounded-lg mb-2 h-14 flex items-center px-3" style={{ backgroundColor: theme.hero }}>
          <span className="text-xs font-bold text-gray-800">Hero Katalog</span>
        </div>
        <div className="grid grid-cols-3 gap-1.5 mb-2">
          {[1, 2, 3].map((n) => (
            <div key={n} className="rounded-md bg-white border border-gray-200 p-1.5">
              <div className="aspect-square rounded bg-gray-100 mb-1" />
              <div className="h-1.5 rounded bg-gray-200 w-3/4" />
            </div>
          ))}
        </div>
        <div className="h-7 rounded-md" style={{ backgroundColor: theme.cta }} />
      </div>
      <div className="px-4 py-3 border-t bg-gray-50 space-y-2">
        <p className="text-[11px] text-gray-500 break-all font-mono">{demo.url}</p>
        <ul className="space-y-1">
          {demo.highlights.map((h) => (
            <li key={h} className="text-xs text-gray-700 flex items-start gap-1.5">
              <Check className="w-3.5 h-3.5 text-green-500 shrink-0 mt-0.5" />
              {h}
            </li>
          ))}
          {demo.locked?.map((h) => (
            <li key={h} className="text-xs text-gray-400 flex items-start gap-1.5">
              <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              {h}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

const PLAN_COMPARE_ROWS = [
  { label: 'Jumlah katalog', free: '1', basic: '1', premium: 'Tanpa batas' },
  { label: 'Jumlah produk', free: '5', basic: '50', premium: 'Tanpa batas' },
  { label: 'Warna katalog', free: 'Default saja', basic: 'Bebas pilih', premium: 'Bebas pilih' },
  { label: 'Badge di katalog', free: '—', basic: 'UMKM Terverifikasi', premium: 'Premium' },
  { label: 'Subdomain sendiri', free: '—', basic: '—', premium: '✓' },
  { label: 'Banner / video promo', free: '—', basic: '—', premium: '✓' },
  { label: 'Statistik pengunjung', free: '—', basic: '—', premium: '✓' },
  { label: 'Tampil di homepage', free: '—', basic: 'Brand pilihan', premium: 'Prioritas utama' },
]

export default function Home() {
  const [brands, setBrands] = useState<any[]>([])
  const [premiumBrands, setPremiumBrands] = useState<any[]>([])
  const [showAllBrands, setShowAllBrands] = useState(false)
  const [loading, setLoading] = useState(true)
  const { data: homeArticles = [] } = usePublicArticles(3)
  const { data: homeTestimonials = [] } = usePublicTestimonials(6)

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await fetch('/api/brands/public')
        if (response.ok) {
          const data = await response.json()
          const sortedBrands = [...data].sort(
            (a: any, b: any) => (b.viewCount || 0) - (a.viewCount || 0)
          )
          setBrands(sortedBrands)
          setPremiumBrands(sortedBrands.filter((b: any) => b.plan === 'premium'))
        }
      } catch (error) {
        console.error('Failed to fetch brands:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchBrands()
  }, [])

  // Get top 10 brands
  const topBrands = brands.slice(0, 10)
  const top5Brands = topBrands.slice(0, 5)
  const bottom5Brands = topBrands.slice(5, 10)

  return (
    <div className="min-h-screen">
      {/* Hero Section dengan Gradient */}
      <section className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        
        <motion.div 
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.div className="text-center" variants={fadeInUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6">
              <Star className="w-4 h-4 text-yellow-300" />
              <span className="text-sm font-medium">Platform katalog digital untuk UMKM Indonesia</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight max-w-4xl mx-auto">
              Bangun Katalog Online Usaha Anda dalam Satu Link
            </h1>
            <p className="text-lg md:text-xl mb-10 text-white/90 max-w-3xl mx-auto leading-relaxed">
              Tampilkan produk, profil usaha, dan kontak pelanggan dalam satu halaman yang mudah dibagikan ke WhatsApp, Instagram, dan Facebook.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-white text-indigo-600 hover:bg-white/90 px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all">
                  Mulai Gratis <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Login
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Trust line */}
          <motion.div 
            className="mt-20 text-center"
            variants={fadeInUp}
          >
            <p className="text-white/80 text-base md:text-lg">
              Digunakan oleh pelaku UMKM di Indonesia
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Pain Points & Solution Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Masih Mengalami Hal Ini?
            </h2>
          </motion.div>

          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {painPoints.map((point, idx) => (
              <motion.div key={idx} variants={fadeInUp}>
                <Card className="h-full border border-red-100 bg-red-50/50 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                      <point.icon className="w-5 h-5 text-red-500" />
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">{point.text}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="text-center max-w-3xl mx-auto bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 md:p-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Satu Link Untuk Semua Produk dan Informasi Usaha Anda
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed">
              UMKM Indonesia membantu Anda membuat halaman katalog digital yang bisa dibagikan ke pelanggan dengan mudah.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Fitur untuk Membantu UMKM Jualan Lebih Mudah
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Semua yang Anda butuhkan untuk menampilkan produk dan menjangkau lebih banyak pelanggan
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {features.map((feature, idx) => (
              <motion.div key={idx} variants={fadeInUp}>
                <Card className="h-full hover:shadow-xl transition-shadow duration-300 border-0 shadow-lg">
                  <CardHeader>
                    <div className={`w-12 h-12 ${feature.color} rounded-xl flex items-center justify-center mb-4`}>
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{feature.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Brand Premium Section */}
      {!loading && premiumBrands.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-amber-50 to-orange-50 border-y border-amber-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div className="text-center mb-10" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-200 text-amber-900 rounded-full mb-4 text-sm font-medium">
                Brand Premium
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Usaha Pilihan Premium</h2>
              <p className="text-gray-600 mt-2">Katalog usaha dengan paket Premium — tampil lebih profesional</p>
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              {premiumBrands.slice(0, 5).map((brand) => (
                <Link key={brand.id} to={`/catalog/${brand.slug}`} target="_blank" rel="noopener noreferrer">
                  <Card className="hover:shadow-lg transition-shadow h-full group">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 rounded-xl overflow-hidden bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
                          {brand.logoUrl ? (
                            <img
                              src={getImageUrl(brand.logoUrl) || ''}
                              alt={brand.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-amber-800 font-bold text-lg">
                              {brand.name?.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="text-xs font-semibold bg-amber-500 text-white px-2 py-0.5 rounded">Premium</span>
                          <h3 className="font-semibold text-gray-900 truncate mt-1 group-hover:text-amber-700 transition-colors">
                            {brand.name}
                          </h3>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{brand.description}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Brand Terpopuler Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full mb-4">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm font-medium">Paling Populer</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Brand Terpopuler
            </h2>
            <p className="text-sm text-indigo-600 font-medium mb-2">
              Contoh katalog yang sudah dibuat oleh pengguna
            </p>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Katalog brand UMKM dengan pengunjung terbanyak
            </p>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <>
              <motion.div 
                className="grid md:grid-cols-2 lg:grid-cols-5 gap-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={staggerContainer}
              >
                {top5Brands.map((brand, idx) => (
                  <motion.div key={brand.id} variants={fadeInUp}>
                    <Link to={`/catalog/${brand.slug}`} target="_blank" rel="noopener noreferrer">
                      <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer group">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              {/* Logo or Initial */}
                              <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center">
                                {brand.logoUrl ? (
                                  <img 
                                    src={getImageUrl(brand.logoUrl) || ''} 
                                    alt={brand.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span className={`text-white font-bold text-lg w-full h-full flex items-center justify-center ${
                                    idx === 0 ? 'bg-yellow-500' :
                                    idx === 1 ? 'bg-gray-400' :
                                    idx === 2 ? 'bg-orange-600' :
                                    'bg-indigo-500'
                                  }`}>
                                    {brand.name.charAt(0).toUpperCase()}
                                  </span>
                                )}
                              </div>
                              {/* Ranking badge */}
                              <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                                idx === 0 ? 'bg-yellow-500' :
                                idx === 1 ? 'bg-gray-400' :
                                idx === 2 ? 'bg-orange-600' :
                                'bg-indigo-500'
                              }`}>
                                {idx + 1}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-gray-900 truncate group-hover:text-indigo-600 transition-colors">
                                {brand.name}
                              </h3>
                              <div className="flex items-center gap-1 text-sm text-gray-500">
                                <Eye className="w-3 h-3" />
                                <span>{brand.viewCount?.toLocaleString()} views</span>
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{brand.description}</p>
                          <div className="flex items-center gap-1 mt-3 text-indigo-600 text-sm font-medium">
                            <span>Lihat Katalog</span>
                            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>

              <motion.div 
                className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mt-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={staggerContainer}
              >
                {bottom5Brands.map((brand, idx) => (
                  <motion.div key={brand.id} variants={fadeInUp}>
                    <Link to={`/catalog/${brand.slug}`} target="_blank" rel="noopener noreferrer">
                      <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer group">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              {/* Logo or Initial */}
                              <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center">
                                {brand.logoUrl ? (
                                  <img 
                                    src={getImageUrl(brand.logoUrl) || ''} 
                                    alt={brand.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-600 font-bold text-lg">
                                    {brand.name.charAt(0).toUpperCase()}
                                  </span>
                                )}
                              </div>
                              {/* Ranking badge */}
                              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-gray-400 flex items-center justify-center text-xs font-bold text-white">
                                {idx + 6}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-gray-900 truncate group-hover:text-indigo-600 transition-colors">
                                {brand.name}
                              </h3>
                              <div className="flex items-center gap-1 text-sm text-gray-500">
                                <Eye className="w-3 h-3" />
                                <span>{brand.viewCount?.toLocaleString()} views</span>
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{brand.description}</p>
                          <div className="flex items-center gap-1 mt-3 text-indigo-600 text-sm font-medium">
                            <span>Lihat Katalog</span>
                            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>

              {!showAllBrands && brands.length > 10 && (
                <motion.div 
                  className="text-center mt-10"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <Button 
                    variant="outline" 
                    size="lg"
                    onClick={() => setShowAllBrands(true)}
                    className="px-8"
                  >
                    Lihat Semua Brand ({brands.length})
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </motion.div>
              )}

              {showAllBrands && (
                <motion.div 
                  className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8"
                  initial="hidden"
                  animate="visible"
                  variants={staggerContainer}
                >
                  {brands.slice(10).map((brand, idx) => (
                    <motion.div key={brand.id} variants={fadeInUp}>
                      <Link to={`/catalog/${brand.slug}`} target="_blank" rel="noopener noreferrer">
                        <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer group">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600 font-bold">
                                {idx + 11}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-gray-900 truncate group-hover:text-indigo-600 transition-colors">
                                  {brand.name}
                                </h3>
                                <div className="flex items-center gap-1 text-sm text-gray-500">
                                  <Eye className="w-3 h-3" />
                                  <span>{brand.viewCount?.toLocaleString()} views</span>
                                </div>
                              </div>
                            </div>
                            <p className="text-sm text-gray-600 mt-2 line-clamp-2">{brand.description}</p>
                          </CardContent>
                        </Card>
                      </Link>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Perbandingan Tampilan Katalog per Paket */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-800 rounded-full mb-4 text-sm font-medium">
              <Palette className="w-4 h-4" />
              Lihat Perbedaannya Langsung
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Beda Paket, Beda Tampilan Katalog
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Pelanggan langsung melihat perbedaan — dari tema warna, badge kepercayaan, subdomain profesional,
              hingga promosi banner di halaman katalog Anda.
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-3 gap-6 mb-14"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {CATALOG_DEMOS.map((demo) => (
              <motion.div key={demo.id} variants={fadeInUp}>
                <CatalogPlanDemo demo={demo} />
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left p-4 font-semibold text-gray-700">Fitur</th>
                  <th className="p-4 font-semibold text-gray-600">Gratis</th>
                  <th className="p-4 font-semibold text-indigo-700">Basic</th>
                  <th className="p-4 font-semibold text-amber-700">
                    <span className="inline-flex items-center gap-1">
                      <Crown className="w-4 h-4" /> Premium
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {PLAN_COMPARE_ROWS.map((row, i) => (
                  <tr key={row.label} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                    <td className="p-4 font-medium text-gray-800">{row.label}</td>
                    <td className="p-4 text-center text-gray-600">{row.free}</td>
                    <td className="p-4 text-center text-indigo-700">{row.basic}</td>
                    <td className="p-4 text-center text-amber-800 font-medium">{row.premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          {!loading && premiumBrands.length > 0 && (
            <motion.p
              className="text-center text-gray-600 mt-8 text-sm"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <BarChart3 className="w-4 h-4 inline mr-1 text-amber-600" />
              Contoh nyata di atas: brand <strong className="text-amber-700">Premium</strong> tampil lebih menonjol,
              lalu diikuti katalog dengan pengunjung terbanyak.
            </motion.p>
          )}
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pilih Paket yang Sesuai
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Mulai gratis dan tingkatkan sesuai kebutuhan bisnis Anda
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {PLANS.map((plan, idx) => (
              <motion.div key={idx} variants={fadeInUp}>
                <Card className={`h-full relative overflow-hidden ${plan.popular ? 'border-2 border-indigo-500 shadow-2xl scale-105' : 'border shadow-lg'}`}>
                  {plan.popular && (
                    <div className="absolute top-0 right-0 bg-indigo-500 text-white px-4 py-1 text-sm font-medium rounded-bl-lg">
                      Rekomendasi
                    </div>
                  )}
                  <CardHeader className="text-center pb-8">
                    <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                    <p className="text-gray-500 text-sm mt-2">{plan.description}</p>
                    <div className="mt-6">
                      <span className="text-4xl font-bold text-gray-900">
                        {plan.price === 0 ? 'Rp0' : `Rp${(plan.price / 1000).toFixed(0)}Rb`}
                      </span>
                      {plan.price > 0 && <span className="text-gray-500">/tahun</span>}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {plan.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center ${plan.popular ? 'bg-indigo-500' : 'bg-green-500'}`}>
                            <Check className="w-3 h-3 text-white" />
                          </div>
                          <span className="text-gray-600 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to={plan.price === 0 ? '/register' : '/subscription'} className="block mt-8">
                      <Button
                        className={`w-full ${plan.popular ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                        variant={plan.popular ? 'default' : 'outline'}
                      >
                        {plan.price === 0 ? 'Mulai Gratis' : `Pilih ${plan.name}`}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Artikel & SEO */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="inline-flex items-center gap-2 text-indigo-600 font-medium mb-3">
              <Newspaper className="h-5 w-5" />
              Artikel & Tips
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pelajari Cara Kembangkan UMKM Anda
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Konten informatif untuk membantu bisnis Anda tumbuh dan lebih mudah ditemukan di Google.
            </p>
          </motion.div>

          {homeArticles.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {homeArticles.map((article: ArticlePreview) => (
                <motion.div key={article.id} variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                  <Card className="h-full hover:shadow-lg transition-shadow">
                    <CardContent className="p-6 flex flex-col h-full">
                      <p className="text-xs text-gray-500 mb-2">
                        {formatArticleDate(article.publishedAt || article.createdAt)}
                      </p>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">{article.title}</h3>
                      <p className="text-gray-600 text-sm flex-1 line-clamp-3 mb-4">
                        {article.excerpt || 'Baca artikel lengkap untuk informasi selengkapnya.'}
                      </p>
                      <Link
                        to={`/artikel/${article.slug}`}
                        className="text-indigo-600 font-medium text-sm inline-flex items-center gap-1 hover:text-indigo-800"
                      >
                        Baca selengkapnya <ChevronRight className="h-4 w-4" />
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500">Artikel akan segera hadir.</p>
          )}

          <div className="text-center mt-10">
            <Link to="/artikel">
              <Button variant="outline" size="lg">
                Lihat Semua Artikel
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimoni */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="inline-flex items-center gap-2 text-amber-600 font-medium mb-3">
              <MessageSquareQuote className="h-5 w-5" />
              Testimoni
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Apa Kata Pelanggan Kami
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Pengalaman nyata dari pelaku UMKM yang sudah menggunakan platform ini.
            </p>
          </motion.div>

          {homeTestimonials.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {homeTestimonials.map((t: PublicTestimonial) => (
                <motion.div
                  key={t.id}
                  variants={fadeInUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <Card className="h-full bg-white shadow-md border-0">
                    <CardContent className="p-6">
                      <div className="flex gap-0.5 mb-3">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${i < (t.rating || 5) ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`}
                          />
                        ))}
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed mb-4 line-clamp-5">
                        &ldquo;{t.content}&rdquo;
                      </p>
                      <p className="font-semibold text-gray-900">{t.displayName}</p>
                      {t.businessName && (
                        <p className="text-sm text-gray-500">{t.businessName}</p>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 max-w-lg mx-auto">
              Belum ada testimoni. Sudah punya akun?{' '}
              <Link to="/login" className="text-indigo-600 font-medium hover:underline">
                Login
              </Link>{' '}
              dan bagikan pengalaman Anda di menu Testimoni.
            </p>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600">
        <motion.div 
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Siap Membuat Usaha Anda Lebih Profesional?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Buat katalog online Anda sekarang dan mulai bagikan ke pelanggan hanya dengan satu link.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-indigo-600 hover:bg-white/90 px-8 py-6 text-lg font-semibold shadow-xl">
              Daftar Gratis Sekarang <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </motion.div>
      </section>

      <SiteFooter />
    </div>
  )
}
